import { type Operator, OperatorSchema } from './operator';
import { $tags } from '@/stores';
import { timeNow } from '@/utils/clock';
import { formatDate, parseTime } from '@/utils/dataUtils';
import { toast } from 'react-toastify';
import { z } from 'zod';

export const CallSignSchema = z.object({
  from: z.string(),
  to: z.string(),
});
export type CallSign = z.infer<typeof CallSignSchema>;
export const FrequencySchema = z.string();
export const DescriptionSchema = z.string();

const BaseEntrySchema = z.object({
  time: z.union([z.string(), z.iso.datetime()]),
  timestamp: z.number().int().nonnegative(),
  deleted: z
    .object({
      operator: OperatorSchema,
      time: z.iso.datetime(),
    })
    .optional(),
});

export const MessageEntrySchema = BaseEntrySchema.extend({
  frequency: FrequencySchema,
  callSign: CallSignSchema,
  description: DescriptionSchema,
  operator: OperatorSchema,
});

export const RadioShiftEntrySchema = BaseEntrySchema.extend({
  oldOperator: OperatorSchema.optional(),
  newOperator: OperatorSchema,
  notes: DescriptionSchema,
});

const BaseLogSchema = z.object({
  id: z.string(),
  draft: z.boolean().default(false),
});

export const MessageLogSchema = BaseLogSchema.extend({
  type: z.literal('message'),
  entries: z.array(MessageEntrySchema),
  tags: z.array(z.string()).optional(),
});

export const RadioShiftLogSchema = BaseLogSchema.extend({
  type: z.literal('radioShift'),
  entries: z.array(RadioShiftEntrySchema),
});

// Generated; can't be changed normally
export const NewDayLogSchema = BaseLogSchema.extend({
  type: z.literal('newDay'),
  day: z.string(),
});

export const LogSchema = z.discriminatedUnion('type', [
  MessageLogSchema,
  RadioShiftLogSchema,
]);

export const VisibleLogSchema = z.discriminatedUnion('type', [
  MessageLogSchema,
  RadioShiftLogSchema,
  NewDayLogSchema,
]);

const ColorSchema = z.string().regex(/^#([0-9a-fA-F]{6}|[0-9a-fA-F]{3})$/);

export const StringOperatorSchema = z.enum([
  'equals',
  'contains',
  'startsWith',
  'endsWith',
  'regex',
]);

export const TagConditionSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('description'),
    value: z.string(),
    operator: StringOperatorSchema,
    caseSensitive: z.boolean(),
  }),
  z.object({
    type: z.literal('frequency'),
    value: z.string(),
    operator: StringOperatorSchema,
    caseSensitive: z.boolean(),
  }),
  z.object({
    type: z.literal('callSign'),
    from: z.string(),
    fromOperator: StringOperatorSchema,
    fromCaseSensitive: z.boolean(),
    to: z.string(),
    toOperator: StringOperatorSchema,
    toCaseSensitive: z.boolean(),
  }),
]);

export const TagRuleSchema = z.object({
  enabled: z.boolean(),
  priority: z.number().int().default(0),
  logic: z.enum(['AND', 'OR']),
  conditions: z.array(TagConditionSchema),
});

export const TagSchema = z.object({
  id: z.string(),
  name: z.string(),
  color: ColorSchema,
  /* @deprecated */
  bgColor: ColorSchema.optional(),
  rules: z.array(TagRuleSchema).optional(),
});

export type MessageEntry = z.infer<typeof MessageEntrySchema>;
export type RadioShiftEntry = z.infer<typeof RadioShiftEntrySchema>;
export type Entry = MessageEntry | RadioShiftEntry;
export type MessageLog = z.infer<typeof MessageLogSchema>;
export type RadioShiftLog = z.infer<typeof RadioShiftLogSchema>;
export type NewDayLog = z.infer<typeof NewDayLogSchema>;
export type Log = z.infer<typeof LogSchema>;
export type VisibleLog = z.infer<typeof VisibleLogSchema>;
export type StringOperator = z.infer<typeof StringOperatorSchema>;
export type TagRule = z.infer<typeof TagRuleSchema>;
export type TagCondition = z.infer<typeof TagConditionSchema>;
export type DescriptionTagCondition = Extract<
  TagCondition,
  {
    type: 'description';
  }
>;
export type FrequencyTagCondition = Extract<
  TagCondition,
  {
    type: 'frequency';
  }
>;
export type CallSignTagCondition = Extract<
  TagCondition,
  {
    type: 'callSign';
  }
>;
export type Tag = z.infer<typeof TagSchema>;

function testRule(log: MessageLog, rule: TagRule): boolean {
  if (rule.logic === 'AND') {
    return rule.conditions.every(condition => testCondition(log, condition));
  }
  return rule.conditions.some(condition => testCondition(log, condition));
}

function testCondition(log: MessageLog, condition: TagCondition): boolean {
  switch (condition.type) {
    case 'description':
      return testDescription(log, condition);
    case 'frequency':
      return testFrequency(log, condition);
    case 'callSign':
      return testCallSign(log, condition);
  }
}

function testDescription(log: MessageLog, condition: DescriptionTagCondition) {
  const entry = log.entries.at(-1);
  return (
    !!entry &&
    testString(
      entry.description,
      condition.value,
      condition.operator,
      condition.caseSensitive,
    )
  );
}

function testFrequency(log: MessageLog, condition: FrequencyTagCondition) {
  const entry = log.entries.at(-1);
  return (
    !!entry &&
    testString(
      entry.frequency,
      condition.value,
      condition.operator,
      condition.caseSensitive,
    )
  );
}

function testCallSign(log: MessageLog, condition: CallSignTagCondition) {
  const entry = log.entries.at(-1);
  return (
    !!entry &&
    (condition.from === '' ||
      testString(
        entry.callSign.from,
        condition.from,
        condition.fromOperator,
        condition.fromCaseSensitive,
      )) &&
    (condition.to === '' ||
      testString(
        entry.callSign.to,
        condition.to,
        condition.toOperator,
        condition.toCaseSensitive,
      ))
  );
}

function testString(
  description: string,
  value: string,
  operator: StringOperator,
  caseSensitive: boolean,
) {
  if (!caseSensitive) {
    description = description.toLowerCase();
    if (operator !== 'regex') value = value.toLowerCase();
  }
  switch (operator) {
    case 'equals':
      return description === value;
    case 'contains':
      return description.includes(value);
    case 'startsWith':
      return description.startsWith(value);
    case 'endsWith':
      return description.endsWith(value);
    case 'regex':
      try {
        if (caseSensitive) return new RegExp(value).test(description);
        return new RegExp(value, 'i').test(description);
      } catch (e) {
        // TODO: move this msg to creation form
        toast.error(`Invalid regex: ${value}`);
        console.error(e);
        return false;
      }
  }
}

function autoAddTags(log: MessageLog): MessageLog {
  if (log.tags?.length) return log;
  const allTags = $tags.get();
  if (allTags.length === 0) return log;

  const rules = allTags.flatMap((tag, tagIndex) =>
    (tag.rules ?? []).map((rule, ruleIndex) => ({
      tag,
      rule,
      tagIndex,
      ruleIndex,
    })),
  );
  rules.sort(
    (a, b) =>
      (b.rule.priority ?? 0) - (a.rule.priority ?? 0) ||
      a.tagIndex - b.tagIndex ||
      a.ruleIndex - b.ruleIndex,
  );

  for (const { tag, rule } of rules) {
    if (rule.enabled && testRule(log, rule)) {
      return { ...log, tags: [tag.id] };
    }
  }

  return log;
}

export class Logs {
  private logs: Log[] = [];
  private operators: Map<string, Operator> = new Map();
  private callSigns: Set<string> = new Set();
  private frequencies: Set<string> = new Set();
  private subscribers: Set<() => void> = new Set();

  private addOperator(o: Operator) {
    if (this.operators.has(o.id)) return;
    this.operators.set(o.id, o);
  }

  private addCallSign(cs: string) {
    this.callSigns.add(cs);
  }

  private addLogStuffs(data: MessageEntry | RadioShiftEntry) {
    if ('operator' in data) {
      this.addOperator(data.operator);
    }
    if ('callSign' in data) {
      this.addCallSign(data.callSign.from);
      this.addCallSign(data.callSign.to);
    }
    if ('oldOperator' in data && data.oldOperator) {
      this.addOperator(data.oldOperator);
    }
    if ('newOperator' in data) {
      this.addOperator(data.newOperator);
    }
    if ('frequency' in data) {
      this.frequencies.add(data.frequency);
    }
  }

  addLog(log: Log) {
    if (log.type === 'message') {
      log = autoAddTags(log);
    }

    this.logs.push(log);

    for (const entry of log.entries) {
      this.addLogStuffs(entry);
    }

    this.emitChange();
  }

  addEntry(logId: string, entry: Entry) {
    const log = this.findLogById(logId);

    if (!log) {
      throw new Error('Log not found');
    }

    if (log.type === 'message' && 'frequency' in entry) {
      log.entries.push(entry);
    } else if (log.type === 'radioShift' && 'oldOperator' in entry) {
      log.entries.push(entry);
    } else {
      throw new Error('Entry type does not match log type');
    }
    this.addLogStuffs(entry);

    this.emitChange();
  }

  deleteLog(id: string, operator: Operator) {
    const log = this.findLogById(id);
    if (!log) {
      throw new Error('Log not found');
    }

    const deletionTime = new Date().toISOString();

    for (const entry of log.entries) {
      entry.deleted = {
        operator,
        time: deletionTime,
      };
    }

    this.emitChange();
  }

  findLogById(id: string): Log | undefined {
    return this.logs.find(log => log.id === id);
  }

  setTags(logId: string, tagIds: string[]) {
    const log = this.findLogById(logId);
    if (!log) {
      throw new Error('Log not found');
    }
    if (log.type !== 'message') {
      throw new Error('Only message logs can have tags');
    }
    log.tags = tagIds;
    this.emitChange();
  }

  undraftLog(id: string) {
    const log = this.findLogById(id);
    if (!log) {
      throw new Error('Log not found');
    }
    const lastEntry = log.entries.at(-1);
    if (lastEntry) {
      const newTime = parseTime(lastEntry.time);
      lastEntry.time = newTime || timeNow();
    }
    log.draft = false;
    this.emitChange();
  }

  getAllLogs(): Log[] {
    return this.logs;
  }

  getNotDeletedLogs(): Log[] {
    return this.logs.filter(
      log => !log.entries.every(entry => entry.deleted !== undefined),
    );
  }

  withGeneratedLogs(): VisibleLog[] {
    const ogLogs = this.getNotDeletedLogs();
    const logs: VisibleLog[] = [];
    let prevDate: number | null = null;
    let generateCount = 0;

    for (const log of ogLogs) {
      const { time } = log.entries.at(-1)!;
      const date = new Date(time);
      const dayDate = Date.UTC(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
      );

      if (!time) {
        logs.push(log);
        continue;
      }

      if (prevDate && dayDate <= prevDate) {
        logs.push(log);
        continue;
      }

      const d = new Date(dayDate);

      prevDate = dayDate;
      logs.push({
        type: 'newDay',
        day: formatDate(d),
        id: d.getTime().toString(),
        draft: false,
      });
      generateCount++;
      logs.push(log);
    }

    if (generateCount <= 1) {
      return ogLogs;
    }

    return logs;
  }

  getOperators(): Operator[] {
    return Array.from(this.operators.values());
  }

  getCallSigns(): string[] {
    return Array.from(this.callSigns);
  }

  getFrequencies(): string[] {
    return Array.from(this.frequencies);
  }

  reset() {
    this.logs = [];
    this.operators.clear();
    this.callSigns.clear();
    this.frequencies.clear();
    this.emitChange();
  }

  subscribe(callback: () => void) {
    this.subscribers.add(callback);

    return () => {
      this.subscribers.delete(callback);
    };
  }

  private emitChange() {
    for (const callback of this.subscribers) {
      callback();
    }
  }

  toJSON() {
    return this.logs;
  }

  loadFromJSON(data: unknown) {
    if (!Array.isArray(data)) {
      throw new Error('Invalid data format');
    }
    this.logs = [];
    this.operators.clear();
    this.callSigns.clear();

    for (const logData of data) {
      const parseResult = LogSchema.safeParse(logData);
      if (!parseResult.success) {
        throw new Error('Invalid log data', { cause: parseResult.error });
      }
      const log = parseResult.data;
      this.logs.push(log);

      for (const entry of log.entries) {
        this.addLogStuffs(entry);
      }
    }

    this.emitChange();
  }

  static fromJSON(data: unknown) {
    const logs = new Logs();
    logs.loadFromJSON(data);
    return logs;
  }
}
